
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Brush } from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { TrendingUp, TrendingDown, Filter, ZoomIn } from 'lucide-react';
import { kgToLbs } from '@/lib/weightConversion';

const chartConfig = {
  weight: {
    label: "💪 Weight (lbs)",
    color: "hsl(220, 90%, 50%)", // Blue
  },
  bodyFat: {
    label: "📊 Body Fat %",
    color: "hsl(0, 90%, 60%)", // Red
  },
  muscleMass: {
    label: "🏋️ Skeletal Muscle Mass (lbs)",
    color: "hsl(120, 90%, 45%)", // Green
  },
};

export const InteractiveBodyCompositionChart: React.FC = () => {
  const { user } = useAuth();
  const [selectedMetrics, setSelectedMetrics] = useState(['weight', 'bodyFat', 'muscleMass']);
  const [timeRange, setTimeRange] = useState('3m'); // 1m, 3m, 6m, 1y, all
  const [showTrend, setShowTrend] = useState(true);

  const { data: bodyCompData, isLoading } = useQuery({
    queryKey: ['enhanced-body-composition', timeRange],
    queryFn: async () => {
      const dateLimit = getDateLimit(timeRange);
      
      let query = supabase
        .from('body_composition')
        .select('recorded_date, total_weight, body_fat_percentage, skeletal_muscle_mass')
        .order('recorded_date', { ascending: true });
      
      if (dateLimit) {
        query = query.gte('recorded_date', dateLimit);
      }

      let { data, error } = await query;
      
      // No fallback to sample data - show empty state if no user data
      
      if (error) throw error;
      
      return data?.map(entry => ({
        date: new Date(entry.recorded_date).toLocaleDateString(),
        fullDate: entry.recorded_date,
        weight: entry.total_weight ? kgToLbs(entry.total_weight) : 0,
        bodyFat: entry.body_fat_percentage || 0,
        muscleMass: entry.skeletal_muscle_mass ? kgToLbs(entry.skeletal_muscle_mass) : 0,
      })) || [];
    },
  });

  const getDateLimit = (range: string) => {
    const now = new Date();
    switch (range) {
      case '1m': return new Date(now.setMonth(now.getMonth() - 1)).toISOString().split('T')[0];
      case '3m': return new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
      case '6m': return new Date(now.setMonth(now.getMonth() - 6)).toISOString().split('T')[0];
      case '1y': return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
      default: return null;
    }
  };

  const calculateTrend = (data: any[], metric: string) => {
    if (data.length < 2) return 0;
    const recent = data.slice(-3).reduce((sum, d) => sum + d[metric], 0) / 3;
    const older = data.slice(0, 3).reduce((sum, d) => sum + d[metric], 0) / 3;
    return ((recent - older) / older * 100);
  };

  const toggleMetric = (metric: string) => {
    setSelectedMetrics(prev => 
      prev.includes(metric) 
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Interactive Body Composition</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center">Loading enhanced chart...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ZoomIn className="h-5 w-5" />
              Interactive Body Composition
            </CardTitle>
            <CardDescription>Advanced analytics with trend analysis</CardDescription>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {['1m', '3m', '6m', '1y', 'all'].map(range => (
              <Button
                key={range}
                variant={timeRange === range ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange(range)}
              >
                {range === 'all' ? 'All' : range.toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Metric toggles */}
          <div className="flex flex-wrap gap-2">
            <Filter className="h-4 w-4 text-gray-500 mt-1" />
            {Object.entries(chartConfig).map(([key, config]) => (
              <Badge
                key={key}
                variant={selectedMetrics.includes(key) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleMetric(key)}
              >
                {config.label}
              </Badge>
            ))}
          </div>

          {/* Trend indicators */}
          {bodyCompData && bodyCompData.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {selectedMetrics.map(metric => {
                const trend = calculateTrend(bodyCompData, metric);
                const config = chartConfig[metric as keyof typeof chartConfig];
                return (
                  <div key={metric} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium">{config.label}</span>
                    <div className="flex items-center gap-1">
                      {trend > 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                      <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Math.abs(trend).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Enhanced chart */}
          {bodyCompData && bodyCompData.length > 0 ? (
            <ChartContainer config={chartConfig} className="h-64 md:h-80 lg:h-96 xl:h-[500px] w-full">
              <LineChart data={bodyCompData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Brush dataKey="date" height={30} />
                
                {selectedMetrics.includes('weight') && (
                  <Line
                    type="monotone"
                    dataKey="weight"
                    stroke="var(--color-weight)"
                    strokeWidth={3}
                    strokeDasharray="0"
                    dot={{ fill: "var(--color-weight)", r: 5, stroke: "#fff", strokeWidth: 2 }}
                    activeDot={{ r: 8, stroke: "var(--color-weight)", strokeWidth: 2 }}
                    name="💪 Weight (lbs)"
                  />
                )}
                
                {selectedMetrics.includes('bodyFat') && (
                  <Line
                    type="monotone"
                    dataKey="bodyFat"
                    stroke="var(--color-bodyFat)"
                    strokeWidth={2}
                    strokeDasharray="8 4"
                    dot={{ fill: "var(--color-bodyFat)", r: 4, stroke: "#fff", strokeWidth: 1 }}
                    activeDot={{ r: 7, stroke: "var(--color-bodyFat)", strokeWidth: 2 }}
                    name="📊 Body Fat %"
                  />
                )}
                
                {selectedMetrics.includes('muscleMass') && (
                  <Line
                    type="monotone"
                    dataKey="muscleMass"
                    stroke="var(--color-muscleMass)"
                    strokeWidth={4}
                    strokeDasharray="2 2"
                    dot={{ fill: "var(--color-muscleMass)", r: 3, stroke: "#fff", strokeWidth: 1 }}
                    activeDot={{ r: 6, stroke: "var(--color-muscleMass)", strokeWidth: 3 }}
                    name="🏋️ Skeletal Muscle Mass (lbs)"
                  />
                )}
              </LineChart>
            </ChartContainer>
          ) : (
            <div className="h-64 md:h-80 lg:h-96 xl:h-[500px] flex items-center justify-center text-gray-500">
              No data available for the selected time range
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
